import styled, { css } from 'styled-components';
import { header2, Text, text2 } from '@ardoq/typography';
import { colors, s24 } from '@ardoq/design-tokens';
import { FlexBox } from '@ardoq/layout';
import { ButtonProps, PrimaryButton } from '@ardoq/button';
import { Icon, IconName } from '@ardoq/icons';
import { TextOverflow } from '@ardoq/popovers';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';
import { ActionMenuAiButton } from './types';
import { AiActionButton } from './AiActionButton';

export const PAGE_TOPBAR_HEIGHT = 96;
export const NEW_PAGE_TOPBAR_HEIGHT = 50;

const TopbarWrapper = styled(FlexBox)<{
  $isNewTopBar: boolean;
}>`
  background-color: ${colors.surfaceDefault};
  color: ${colors.textDefault};
  ${({ $isNewTopBar }) =>
    $isNewTopBar &&
    css`
      border-bottom: 1px solid ${colors.borderSubtle};
    `}
  height: ${({ $isNewTopBar }) =>
    $isNewTopBar ? `${NEW_PAGE_TOPBAR_HEIGHT}px` : `${PAGE_TOPBAR_HEIGHT}px`};
  min-height: ${({ $isNewTopBar }) =>
    $isNewTopBar ? `${NEW_PAGE_TOPBAR_HEIGHT}px` : `${PAGE_TOPBAR_HEIGHT}px`};
`;

const Right = styled(FlexBox).attrs({
  align: 'center',
  justify: 'flex-end',
  gap: 'medium',
})`
  flex: 1 1 auto;
  margin-left: auto;
`;

const SecondaryText = styled.span`
  ${text2};
  color: ${colors.textSubtle};
`;

const PrimaryText = styled.h2`
  ${header2};
  color: ${colors.textDefault};
  margin: 0;
`;

export const ToolbarDivider = styled.hr`
  background-color: ${colors.borderSubtle};
  height: ${s24};
  width: 1px;
  border-style: none;
  margin: 0;
`;

type CreateNewButtonProps = ButtonProps & {
  i18nLocale?: I18nLocale;
};

export const CreateNewButton = ({
  children,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
  ...props
}: CreateNewButtonProps) => {
  return (
    <PrimaryButton {...props}>
      {children ??
        t(i18nLocale, 'pageLayout:pageTopbar.createNew', 'Create new')}
      <Icon iconName={IconName.ADD} />
    </PrimaryButton>
  );
};

export type PageTopbarProps = {
  toggleSidebarButton?: React.ReactNode;
  primaryContent: React.ReactNode;
  secondaryContent: React.ReactNode;
  primaryButton?: React.ReactNode;
  secondaryButton?: React.ReactNode;
  aiActionItem?: ActionMenuAiButton | null;
  toolbarContent?: React.ReactNode;
  middleContent?: React.ReactNode;
  className?: string;
};

const NewLeftContent = ({
  toggleSidebarButton,
  primaryContent,
  secondaryContent,
}: Pick<
  PageTopbarProps,
  'toggleSidebarButton' | 'primaryContent' | 'secondaryContent'
>) => {
  const shouldRenderWithTextOverflow = typeof primaryContent === 'string';
  return (
    <FlexBox gap="small" align="center">
      {toggleSidebarButton}
      {secondaryContent && <Text color="textSubtle">{secondaryContent}</Text>}
      {shouldRenderWithTextOverflow ? (
        <TextOverflow lineClamp={1}>
          <Text variant="text2Bold">{primaryContent}</Text>
        </TextOverflow>
      ) : (
        <Text variant="text2Bold">{primaryContent}</Text>
      )}
    </FlexBox>
  );
};

const LeftContent = ({
  primaryContent,
  secondaryContent,
}: Pick<PageTopbarProps, 'primaryContent' | 'secondaryContent'>) => {
  // primaryContent can be other things than string, e.g. in the use case landing page it's a dropdown. So we don't want to apply text overflow to non-string content.
  const shouldRenderWithTextOverflow = typeof primaryContent === 'string';

  return (
    <FlexBox flexDirection="column" gap="xsmall" style={{ overflow: 'hidden' }}>
      <SecondaryText>{secondaryContent}</SecondaryText>
      {shouldRenderWithTextOverflow ? (
        <TextOverflow lineClamp={1}>
          <PrimaryText>{primaryContent}</PrimaryText>
        </TextOverflow>
      ) : (
        <PrimaryText>{primaryContent}</PrimaryText>
      )}
    </FlexBox>
  );
};

/**
 * @deprecated Use NavBar instead
 */
const PageTopbar = ({
  toolbarContent,
  toggleSidebarButton,
  primaryContent,
  secondaryContent,
  primaryButton,
  secondaryButton,
  middleContent,
  className,
  aiActionItem,
}: PageTopbarProps) => {
  const aiButton = aiActionItem ? (
    <AiActionButton action={aiActionItem} />
  ) : null;
  return (
    <TopbarWrapper
      role="banner"
      className={className}
      paddingX="large"
      align="center"
      justify="space-between"
      width="full"
      $isNewTopBar={Boolean(toggleSidebarButton)}
    >
      {toggleSidebarButton ? (
        <NewLeftContent
          toggleSidebarButton={toggleSidebarButton}
          primaryContent={primaryContent}
          secondaryContent={secondaryContent}
        />
      ) : (
        <LeftContent
          primaryContent={primaryContent}
          secondaryContent={secondaryContent}
        />
      )}
      {middleContent}
      <Right>
        {toolbarContent}
        {secondaryButton || primaryButton || aiButton ? (
          <FlexBox gap="small" align="center">
            {secondaryButton}
            {primaryButton}
            {aiButton}
          </FlexBox>
        ) : null}
      </Right>
    </TopbarWrapper>
  );
};

export { PageTopbar };
